import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ProductGallery from "@/components/ProductGallery";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";
import { Leaf, Droplet, Sun, Sparkles, ArrowRight, Star } from "lucide-react";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { gift_box_price, bulk_box_price, hero_image_url } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const bestsellers = products.slice(0, 3);
  const heroSrc = hero_image_url || "https://images.unsplash.com/photo-1664791461482-79f5deee490f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxtYW5nbyUyMHBpY2tsZSUyMGNoaWxpJTIwcGlja2xlJTIwZ2xhc3MlMjBqYXIlMjBmb29kfGVufDB8fHx8MTc4ODAzMTQ1NXww&ixlib=rb-4.1.0&q=85";

  return (
    <div className="fade-up">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FBF8F3] to-[#F4EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <p data-testid="hero-eyebrow" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9E3B21] mb-5">
                ✦ Est. In Dadu's Kitchen
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1E1B18] leading-[1.05]">
                Pickles the way <em className="text-[#9E3B21] not-italic">Dadu</em> made them.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#6E675F] leading-relaxed max-w-xl">
                Cured in organic cold-pressed mustard oil. Steeped in sunlight. Zero preservatives.
                Small home-batches of seven ancestral achaars — from raw mango to Banarasi red chili.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  data-testid="hero-shop-btn"
                  onClick={() => navigate("/shop")}
                  className="btn-primary rounded-full px-8 py-6 text-base"
                >
                  Explore the Jars <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  data-testid="hero-story-btn"
                  onClick={() => navigate("/about")}
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base border-[#1E1B18] hover:bg-[#1E1B18] hover:text-[#FBF8F3]"
                >
                  Our Story
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs text-[#6E675F]">
                <div className="flex items-center gap-1.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#E5A83B] text-[#E5A83B]" />)}
                  <span className="ml-1">4.9 from home kitchens</span>
                </div>
                <div className="hidden sm:block">🚚 Delhi NCR</div>
                <div className="hidden sm:block">Homemade in Meerut</div>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <div className="relative aspect-[4/5] max-w-lg mx-auto rounded-3xl overflow-hidden shadow-2xl grain-bg">
                <img
                  src={heroSrc}
                  alt="Homemade pickle"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 rotate-[-4deg]">
                <div className="w-10 h-10 rounded-full bg-[#5F7355] flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#5F7355]">Organic</div>
                  <div className="text-xs text-[#6E675F]">Cold-pressed mustard oil</div>
                </div>
              </div>
              <div className="absolute -top-4 -right-2 sm:-right-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 rotate-[6deg]">
                <div className="w-10 h-10 rounded-full bg-[#D9822B] flex items-center justify-center">
                  <Sun className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#D9822B]">Sun-cured</div>
                  <div className="text-xs text-[#6E675F]">In glass barnis</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust pillars */}
      <section className="py-16 lg:py-20 bg-[#FBF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Leaf, title: "Certified Organic", desc: "Every spice, every ingredient — sourced from certified organic farms.", color: "#5F7355" },
              { icon: Droplet, title: "Organic Cold-Pressed Oils", desc: "Kachi ghani mustard oil pressed slowly to keep aroma and nutrition intact.", color: "#D9822B" },
              { icon: Sparkles, title: "Zero Preservatives", desc: "No colours, no chemicals. Just spices, sunlight, and time.", color: "#9E3B21" },
            ].map((p) => (
              <div
                key={p.title}
                data-testid={`trust-pillar-${p.title.toLowerCase().replace(/\s/g, '-')}`}
                className="bg-white rounded-2xl border border-[#E6DEC3] p-8 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${p.color}18` }}>
                  <p.icon className="w-6 h-6" style={{ color: p.color }} />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-2">{p.title}</h3>
                <p className="text-sm text-[#6E675F] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      <section className="py-16 lg:py-24 bg-[#F4EFE6]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9E3B21] mb-3">✦ Bestsellers</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold">Loved from Dilli to Delhi</h2>
            </div>
            <Link to="/shop" data-testid="bestsellers-view-all" className="text-sm font-semibold text-[#9E3B21] flex items-center gap-1 hover:gap-2 transition-all">
              View all 7 pickles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => <div key={i} className="h-96 bg-white rounded-2xl animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {bestsellers.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* Gift Box + Family Pack Promos */}
      <section className="py-16 lg:py-20 bg-[#FBF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#9E3B21] to-[#7F2D17] text-[#FBF8F3] p-8 md:p-10 flex flex-col justify-between min-h-[280px]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E5A83B] mb-3">✦ Gift Box · {formatINR(gift_box_price || 650)}</p>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
                Pick 3 pickles. Add a note. Send love.
              </h3>
              <p className="mt-3 text-[#F4EFE6]/80 text-sm max-w-md">
                Perfect for Diwali, weddings, and homesick hearts.
              </p>
            </div>
            <div>
              <Button
                data-testid="home-giftbox-cta"
                onClick={() => navigate("/gift-box")}
                className="rounded-full px-6 py-5 bg-[#E5A83B] hover:bg-[#D9822B] text-[#1E1B18] font-semibold"
              >
                Build a Gift Box <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#5F7355] to-[#3F5238] text-[#FBF8F3] p-8 md:p-10 flex flex-col justify-between min-h-[280px]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E5A83B] mb-3">✦ Family Pack · {formatINR(bulk_box_price || 1250)}</p>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
                Six jars for a bigger table.
              </h3>
              <p className="mt-3 text-[#F4EFE6]/80 text-sm max-w-md">
                Load up on favourites — max 2 jars per pickle for variety.
              </p>
            </div>
            <div>
              <Button
                data-testid="home-bulkbox-cta"
                onClick={() => navigate("/bulk-box")}
                className="rounded-full px-6 py-5 bg-[#E5A83B] hover:bg-[#D9822B] text-[#1E1B18] font-semibold"
              >
                Build a Family Pack <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <ProductGallery />

      {/* Story strip */}
      <section className="py-20 lg:py-28 bg-[#1E1B18] text-[#FBF8F3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E5A83B] mb-5">✦ Our Craft</p>
          <blockquote className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-relaxed italic">
            "Every jar carries the fingerprints of a home kitchen — no machines, no factories.
            Just Dadu's grandmother's recipe, mustard oil warming in a kadhai, and a bowl of sun-dried mangoes waiting to be pickled."
          </blockquote>
          <Button
            data-testid="story-read-more"
            onClick={() => navigate("/about")}
            className="btn-primary rounded-full mt-10 px-8 py-6"
          >
            Read the full story <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
